import { useLocation, useParams, Link } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import TicketCountChart from '../helpers/TicketChart';
import axiosInstance from '../api/axiosInstance';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function IndividualSite() {
  const { siteid, sitename } = useParams();
  const location = useLocation();
  const [siteDetails, setSiteDetails] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [isWeatherLoading, setIsWeatherLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [eventsFiltered, setEventsFiltered] = useState([]);
  const [ticketsCurrentGrouped, setTicketsCurrentGrouped] = useState([]);
  const [ticketsHourGrouped, setTicketsHourGrouped] = useState([]);
  const [scores, setScores] = useState(location.state?.scoreBreakdown || {
    base_score: 0,
    weather_score: 0,
    boost_score: 0,
    event_score: 0,
    route_score: 0,
    final_score: 0,
    current_period: "",
    day_type: ""
  });
  const isBoosted = location.state?.is_boost || false;
  const routeInfo = location.state?.route || null;

  // Fetch site details
  useEffect(() => {
    const fetchSiteDetails = async () => {
      try {
        const response = await axiosInstance.get(`/api/v1/sitesdetailed/${siteid}/`);
        if (response.data) {
          setSiteDetails(response.data);
        }
      } catch (error) {
        console.error("Error fetching site details:", error);
      }
    };
    fetchSiteDetails();
  }, [siteid]);

  // Fetch weather data
  useEffect(() => {
    const fetchWeather = async () => {
      setIsWeatherLoading(true);
      try {
        const now = new Date();
        console.log("Fetching weather for site:", siteid);
        const response = await axiosInstance.post('/api/v1/sitesweather/single/', {
          site_id: parseInt(siteid),
          current_time: now.toISOString()
        });
        console.log("Weather API response:", response.data);
        if (response.data) {
          setWeatherData(response.data);
          console.log("Weather data set:", response.data);
        }
      } catch (error) {
        console.error("Error fetching weather data:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      } finally {
        setIsWeatherLoading(false);
      }
    };
    fetchWeather();
  }, [siteid]);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axiosInstance.post('/api/v1/ticketmasterevents/', {
          siteids: [siteid]
        });
        const eventsData = Array.isArray(response.data) ? response.data : [];
        setEvents(eventsData);
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };
    fetchEvents();
  }, [siteid]);

  // Fetch ticket data
  // useEffect(() => {
  //   const fetchGrouped = async () => {
  //     try {
  //       const res = await axiosInstance.post('/api/v1/tickets/6month-grouped-current/', {
  //         siteids: [siteid],
  //         currentPeriod: [0, 24],
  //         isWeekday: new Date().getDay() < 6
  //       });
  //       console.log("6 month data:", res.data);
  //       setTicketsCurrentGrouped(Array.isArray(res.data) ? res.data : []);
  //     } catch (e) {
  //       console.error("6 month data error:", e);
  //     }
  //   };
  //   fetchGrouped();
  // }, [siteid]);

  // Fetch hourly ticket data
  // useEffect(() => {
  //   const fetchHour = async () => {
  //     try {
  //       const res = await axiosInstance.post('/api/v1/tickets/hour-grouped/', {
  //         siteids: [siteid]
  //       });
  //       setTicketsHourGrouped(Array.isArray(res.data) ? res.data : []);
  //     } catch (e) {
  //       console.error("Hour data error:", e);
  //     }
  //   };
  //   fetchHour();
  // }, [siteid]);

  const filterCurrentEvents = useCallback(() => {
    const rightNow = new Date(); // Current time

    const startOfToday = new Date(rightNow);
    startOfToday.setHours(0, 0, 0, 0);

    const endOfToday = new Date(rightNow);
    endOfToday.setHours(23, 59, 59, 999);

    const filtered = events.filter(event => {
      if (!event.startdatetime || !event.enddatetime) {
        return false; // Ensure events have valid start and end times
      }
      const eventStart = new Date(event.startdatetime);
      const eventEnd = new Date(event.enddatetime);

      // Check if the event is currently happening
      const isCurrentlyActive = rightNow >= eventStart && rightNow <= eventEnd;
      
      // Check if the event has some overlap with today (relevant for the section title)
      const eventOverlapsToday = eventStart <= endOfToday && eventEnd >= startOfToday;

      return isCurrentlyActive && eventOverlapsToday;
    });
    setEventsFiltered(filtered);
  }, [events]);

  useEffect(() => { filterCurrentEvents(); }, [filterCurrentEvents]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <Link to="/patrolplan" className="text-sm text-customIndigo hover:underline">← Back to Patrol Plan</Link>

      <div className={`bg-white rounded-lg shadow-md divide-y divide-gray-200 ${isBoosted ? 'border-2 border-[#4eb300]' : ''}`}>
        {/* Header Section */}
        <div className={`p-4 flex items-center gap-4 ${isBoosted ? 'bg-[#4eb300]/5' : 'bg-gray-50/50'}`}>
          {isWeatherLoading ? (
            <div className="w-12 h-12 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-customIndigo border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : weatherData?.weather_icon && (
            <img src={`https://openweathermap.org/img/wn/${weatherData.weather_icon}@2x.png`} alt="Weather" className="w-12 h-12" />
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl text-gray-900 font-medium">{decodeURIComponent(sitename)}</h1>
              {isBoosted && (
                <span className="px-2 py-0.5 text-xs font-medium text-[#4eb300] bg-[#4eb300]/10 rounded-full">
                  Boosted
                </span>
              )}
            </div>
            {isWeatherLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 border-2 border-customIndigo border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 text-xs">Loading weather...</p>
              </div>
            ) : (
              <div className="text-xs text-gray-500 space-y-0.5">
                {weatherData?.weather_main && (
                  <p>{weatherData.weather_main} — {weatherData.weather_description}</p>
                )}
                {weatherData?.temp && (
                  <p>Temperature: {weatherData.temp}°C</p>
                )}
              </div>
            )}
            {isBoosted && (
              <p className="text-[#4eb300] text-sm font-medium mt-1">Boost Score: +{scores.boost_score}</p>
            )}
          </div>
        </div>

        {/* Score and Route Section */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
          <div className={`space-y-2 p-3 rounded-md border ${isBoosted ? 'border-[#4eb300]/20 bg-[#4eb300]/5' : 'border-gray-100 bg-gray-50/50'}`}>
            <h3 className="text-sm font-medium text-gray-900">Score Breakdown</h3>
            <div className="text-xs space-y-1 text-gray-500">
              <div className="flex justify-between">
                <span>Base Score</span>
                <span>{scores.base_score.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Weather Score</span>
                <span>{scores.weather_score.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Boost Score</span>
                <span className={isBoosted ? 'text-[#4eb300] font-medium' : ''}>
                  {scores.boost_score.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Event Score</span>
                <span>{scores.event_score.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Route Score</span>
                <span>{scores.route_score.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-medium text-gray-900 pt-1 border-t border-gray-200">
                <span>Final Score</span>
                <span>{scores.final_score.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-2 p-3 rounded-md border border-gray-100 bg-gray-50/50">
            <h3 className="text-sm font-medium text-gray-900">Route Info</h3>
            <div className="text-xs space-y-1 text-gray-500">
              {routeInfo ? (
                <>
                  <div className="flex justify-between">
                    <span>Distance</span>
                    <span>{routeInfo.distance_str}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Duration</span>
                    <span>{routeInfo.duration_str}</span>
                  </div>
                </>
              ) : (
                <div className="text-gray-400 italic">Route information not available</div>
              )}
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="p-4 bg-gray-50/50">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Site Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-xs text-gray-500 p-3 rounded-md border border-gray-100 bg-white">
            <div className="flex justify-between">
              <span>Address 1</span>
              <span>{siteDetails?.addressline1}</span>
            </div>
            <div className="flex justify-between">
              <span>Address 2</span>
              <span>{siteDetails?.addressline2}</span>
            </div>
            <div className="flex justify-between">
              <span>Address 3</span>
              <span>{siteDetails?.addressline3 || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span>Address 4</span>
              <span>{siteDetails?.addressline4 || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span>Area</span>
              <span>{siteDetails?.area_name || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span>City</span>
              <span>{siteDetails?.city}</span>
            </div>
            <div className="flex justify-between">
              <span>County</span>
              <span>{siteDetails?.county || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span>Postcode</span>
              <span>{siteDetails?.postcode}</span>
            </div>
            <div className="flex justify-between">
              <span>Country</span>
              <span>{siteDetails?.countrycode}</span>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        {/* <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
          {ticketsCurrentGrouped.length > 0 && (
            <div className="p-3 rounded-md border border-gray-100 bg-gray-50/50">
              <h3 className="text-sm font-medium text-gray-900 mb-1">Monthly Tickets</h3>
              <p className="text-xs text-gray-500 mb-2">
                {scores.day_type ? scores.day_type.charAt(0).toUpperCase() + scores.day_type.slice(1) : 'All Time'}, {scores.current_period || '12am - 12pm'} {'(Affixed, Drive Off)'}
              </p>
              <TicketCountChart data={ticketsCurrentGrouped} name='monthChart' />
            </div>
          )}

          {ticketsHourGrouped.length > 0 && (
            <div className="p-3 rounded-md border border-gray-100 bg-gray-50/50">
              <h3 className="text-sm font-medium text-gray-900 mb-1">Hourly Tickets</h3>
              <p className="text-xs text-gray-500 mb-2">All Time (All ticket types)</p>
              <TicketCountChart data={ticketsHourGrouped} name='hourChart' />
            </div>
          )}
        </div> */}

        {/* Events Section */}
        <div className="p-4 bg-gray-50/50">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Active Events</h3>
          {scores.event_score > 0 ? (
            <>
              <p className="text-xs text-gray-500 mb-4">Total currently active: {eventsFiltered.length}</p>
              {eventsFiltered.length > 0 ? (
                <div className="space-y-3">
                  {eventsFiltered.map((event, index) => (
                    <div key={event.id || index} className="p-3 rounded-md border border-gray-100 bg-white shadow-sm">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">{event.name}</h4>
                      <div className="text-xs text-gray-500 space-y-0.5">
                        <p className="font-medium">{event.venue_name}</p>
                        <p>
                          {[
                            event.venue_address_line_1,
                            event.venue_address_line_2,
                            event.venue_city,
                            event.venue_postcode
                          ].filter(Boolean).join(', ')}
                        </p>
                        <div className="mt-1 text-gray-400">
                          <p>{new Date(event.startdatetime).toLocaleString()} → {new Date(event.enddatetime).toLocaleString()}</p>
                          <p>Distance: {event.distance} miles</p>
                        </div>
                      </div>
                      <a href={event.url} target="_blank" rel="noreferrer" className="text-customIndigo hover:underline text-xs mt-2 inline-block">
                        {event.url}
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No events currently active for today.</p>
              )}
            </>
          ) : (
            <div className="p-3 rounded-md border border-gray-100 bg-white shadow-sm">
              <p className="text-xs text-gray-500">No events in this area.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default IndividualSite;
